<template>
  <div class="na-page">
    <div class="na-card">
      <section class="na-section">
        <div class="na-section-title">
          <span class="na-section-title__bar" />
          入网确认
        </div>
        <el-form
          ref="queryForm"
          :model="form"
          :inline="true"
          class="na-query-form"
          label-width="5.5rem"
          @submit.native.prevent
        >
          <div class="na-query-line">
            <el-form-item label="车牌号">
              <el-input
                v-model="form.vno"
                class="na-field"
                placeholder="请输入车牌号"
                clearable
                @input="invalidateQuery"
                @keyup.enter.native="onSearch"
              />
            </el-form-item>
            <el-form-item label="车牌颜色">
              <el-select
                v-model="form.vco"
                class="na-field"
                placeholder="请选择车牌颜色"
                clearable
                @change="invalidateQuery"
              >
                <el-option
                  v-for="o in plateColorOptions"
                  :key="o.value"
                  :label="o.label"
                  :value="o.value"
                />
              </el-select>
            </el-form-item>
          </div>
          <div class="na-query-line">
            <el-form-item label="车架号">
              <el-input
                v-model="form.vin"
                class="na-field"
                placeholder="请输入车架号"
                clearable
                maxlength="17"
                @input="invalidateQuery"
                @keyup.enter.native="onSearch"
              />
            </el-form-item>
            <el-form-item label-width="0" class="na-query-btns-item">
              <el-button type="primary" :loading="loading" @click="onSearch">
                查询
              </el-button>
              <el-button @click="onReset">重置</el-button>
            </el-form-item>
          </div>
        </el-form>
        <p class="na-tip">
          说明：车牌号与车牌颜色为一组，车架号单独一组，二选一输入即可；都填时以车牌号为准
        </p>
      </section>

      <div class="na-divider" />

      <section class="na-section">
        <div class="na-section-title">
          <span class="na-section-title__bar" />
          查询结果
        </div>
        <div v-loading="loading" class="na-result">
          <el-empty v-if="!hasQueried" description="请填写查询条件后点击查询" />
          <el-descriptions
            v-else
            class="na-deadline-result"
            :column="1"
            border
            size="medium"
          >
            <el-descriptions-item label="是否入网">
              {{ networkStatusText }}
            </el-descriptions-item>
            <el-descriptions-item v-if="isNetworked" label="最后定位时间">
              {{ lastLocationTimeText }}
            </el-descriptions-item>
            <el-descriptions-item v-if="queryError" label="失败原因">
              {{ queryError }}
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'
import nativePlateColorsMixin from '@/mixins/nativePlateColors'

const QUERY_URL = '/webserver/checkVehicleStatus'

function isOk(res) {
  return res && res.success === true
}

function extractDeadline(res) {
  return res && res.data != null ? res.data : null
}

export default {
  name: 'NetworkAccessPage',
  mixins: [nativePlateColorsMixin],
  data() {
    return {
      loading: false,
      hasQueried: false,
      deadline: null,
      queryError: '',
      requestSequence: 0,
      form: {
        vno: '',
        vco: '2',
        vin: ''
      }
    }
  },
  computed: {
    networkState() {
      if (this.queryError) return 'FAILED'
      if (typeof this.deadline !== 'string' && typeof this.deadline !== 'number') return 'UNKNOWN'
      const raw = String(this.deadline).trim().toLowerCase()
      if (raw === 'no') return 'NOT_ENROLLED'
      if (raw === 'yes') return 'ENROLLED'
      const time = Number(raw)
      return /^\d{13}$/.test(raw) && Number.isFinite(time) && Number.isInteger(time) && time > 0 && time <= Date.now()
        ? 'ENROLLED'
        : 'UNKNOWN'
    },
    isNetworked() {
      return this.networkState === 'ENROLLED'
    },
    networkStatusText() {
      return {
        ENROLLED: '已入网',
        NOT_ENROLLED: '未入网',
        UNKNOWN: '未知',
        FAILED: '查询失败'
      }[this.networkState]
    },
    lastLocationTimeText() {
      if (!this.isNetworked) return '—'
      const raw = String(this.deadline).trim()
      if (!/^\d{13}$/.test(raw)) return '—'
      const n = Number(raw)
      const d = this.$dayjs(n)
      return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '—'
    }
  },
  beforeDestroy() {
    this.requestSequence++
  },
  methods: {
    onPlateColorAccountChanged() { this.invalidateQuery(); this.form = { vno: '', vco: '2', vin: '' } },
    invalidateQuery() {
      this.requestSequence++
      this.loading = false
      this.hasQueried = false
      this.deadline = null
      this.queryError = ''
    },
    buildQueryPayload() {
      const vno = (this.form.vno || '').trim()
      const vco =
        this.form.vco !== '' && this.form.vco != null
          ? String(this.form.vco).trim()
          : ''
      const fd = new FormData()
      // 默认颜色不代表输入了车牌；完整牌色优先，不校验无关VIN。
      if (vno) {
        if (!this.canSelectPlateColor(vco)) {
          return { error: this.nativePlateColorsError || '请同时填写车牌号和正常启用的车牌颜色' }
        }
        fd.append('vclN', `${vno}_${vco}`)
      } else {
        const vin = (this.form.vin || '').trim().toUpperCase()
        if (!vin) {
          return { error: '请填写车牌号+车牌颜色，或车架号（二选一）' }
        }
        if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
          return { error: '车架号应为17位字母或数字，不含I、O、Q' }
        }
        fd.append('vin', vin)
      }
      return { fd }
    },
    async onSearch() {
      const built = this.buildQueryPayload()
      if (built.error) {
        this.$message.warning(built.error)
        return
      }
      this.invalidateQuery()
      const sequence = this.requestSequence
      const query = JSON.stringify(this.form)
      const account = this.nativePlateColorAccount
      const isCurrent = () => sequence === this.requestSequence && account === this.nativePlateColorAccount && query === JSON.stringify(this.form)
      this.loading = true
      try {
        const res = await request.post(QUERY_URL, built.fd)
        if (!isCurrent()) return
        if (!isOk(res)) {
          throw new Error((res && res.msg) || '查询失败')
        }
        this.deadline = extractDeadline(res)
        this.hasQueried = true
        if (this.networkState === 'UNKNOWN') {
          this.$message.warning('暂无法确认入网状态')
        } else {
          this.$message.success((res && res.msg) || '查询成功')
        }
      } catch (e) {
        if (!isCurrent()) return
        this.queryError = (e && (e.msg || e.message)) || '查询失败，请重试'
        this.hasQueried = true
        this.$message.error(this.queryError)
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    onReset() {
      this.invalidateQuery()
      this.form = { vno: '', vco: this.defaultPlateColor(), vin: '' }
    }
  }
}
</script>

<style scoped>
.na-page {
  padding: 1rem;
  min-height: calc(100vh - 7rem);
  box-sizing: border-box;
  background: #f5f7fa;
}

.na-card {
  background: #fff;
  border-radius: 4px;
  padding: 1.25rem 1.5rem 1.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.na-section-title {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.na-section-title__bar {
  display: inline-block;
  width: 4px;
  height: 16px;
  margin-right: 8px;
  background: var(--layout-primary, #087dff);
  border-radius: 2px;
}

.na-query-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.na-query-form >>> .el-form-item {
  margin-right: 1.25rem;
  margin-bottom: 0.75rem;
}

.na-query-form >>> .el-form-item__label {
  color: #606266;
}

.na-query-form >>> .na-field {
  width: 14rem;
}

.na-query-btns-item {
  margin-right: 0 !important;
}

.na-query-btns-item >>> .el-form-item__content {
  display: flex;
  gap: 0.5rem;
}

.na-tip {
  margin: 0.25rem 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #f56c6c;
}

.na-divider {
  height: 1px;
  margin: 1.25rem 0;
  background: #ebeef5;
}

.na-result {
  min-height: 120px;
}

.na-deadline-result {
  max-width: 28rem;
}
</style>
